import * as Events from './Events';
import { SessionDTO, Permission, InstallationType, WhenPrint, DefaultLocationInSessionDTO, WhenAskForFriendlyName, WhenAskForTableCompanions, OnSelectLocationAction, ScaleType, FlowAction, TicketLocationDTO, TipSettingsMode } from '../api';
import * as Localize from './Localize';

let currentSession: Nullable<SessionDTO>;
let introducedDistributorToolsPassword = '';
let cachedShowLastSaleInfoInLoginView = false;

// Necesito guardarlo fuera de session porque me hace falta en la pantalla de login,
// donde aún no hay una sesión iniciada.
let attendanceModuleEnabled = false;

export function getCurrent(): SessionDTO {
	if (!currentSession)
		throw Error('No se puede acceder a la sesión porque no ha sido inicializada. Para comprobar si está iniciada o no, hay que usar isSessionStarted');

	return currentSession;
}

export function setAttendanceModuleEnabled(enabled: boolean): void {
	attendanceModuleEnabled = enabled;
}

export function distributorToolsPasswordIntroduced(newPassword: string) {
	introducedDistributorToolsPassword = newPassword;
}

export function currentIntroducedDistributorToolsPassword(): string {
	return introducedDistributorToolsPassword;
}

export async function clearCurrent(): Promise<void> {
	currentSession = null;
	introducedDistributorToolsPassword = '';
	await Localize.resetLanguageCode(async (languageCode) => await Events.raise('language:changed', languageCode));
}

export async function setCurrent(session: SessionDTO): Promise<void> {
	currentSession = session;
	await Localize.setLanguageCode(session.LanguageCode, async (languageCode: string) => await Events.raise('language:changed', languageCode));
	cachedShowLastSaleInfoInLoginView = session.ShowLastSaleInLoginView;
}

export function getAfterCloseTicketAction(location: TicketLocationDTO): FlowAction {

	const action = getCurrent().AfterCloseTicketAction;
	if (action === FlowAction.DoNothing && (location.AskForTableCompanions === WhenAskForTableCompanions.OnOpenDocument || location.StartTakeOutOrderOnOpenTicket || location.AskForFriendlyName === WhenAskForFriendlyName.OnOpenTicket))
		return FlowAction.ChangeLocation;

	return action;
}

export function isSessionStarted(): boolean {
	return currentSession != null;
}

export function isSchedulePreparationEnabled(): boolean {
	return !!currentSession && currentSession.EnableSchedulePreparation;
}

export function isSaleFormatModuleEnabled(): boolean {
	return !!currentSession && currentSession.EnableSaleFormatsModule;
}

export function isStockModuleEnabled(): boolean {
	return !!currentSession && currentSession.EnableStockModule;
}

export function isTraceabilityEnabled(): boolean {
	return !!currentSession && currentSession.EnableTraceabilityModule;
}

export function isCctvModuleEnabled(): boolean {
	return !!currentSession && currentSession.EnableCctvModule;
}

export function isKitchenDisplayModuleEnabled(): boolean {
	return !!currentSession && currentSession.EnableKitchenDisplayModule;
}

export function isPriceListSchedulerModuleEnabled(): boolean {
	return !!currentSession && currentSession.EnablePriceListSchedulerModule;
}

export function isEmailingModuleEnabled(): boolean {
	return !!currentSession && currentSession.EnableEmailingModule;
}

export function isSmartMenuEnabled(): boolean {
	return !!currentSession && currentSession.EnableSmartMenu;
}

export function isDeliveryEnabled(): boolean {
	return !!currentSession && currentSession.EnableDelivery;
}

export function isScaleSystemEnabled(): boolean {
	return !!currentSession && currentSession.EnableScaleSystemModule;
}

export function getScaleSystemType(): ScaleType {
	return currentSession ? currentSession.ScaleSystemType : ScaleType.Epelsa;
}

export function isTicketBAIEnabled(): boolean {
	return !!currentSession && currentSession.EnableTicketBAIModule;
}

export function isCardProcessorModuleEnabled(): boolean {
	return !!currentSession && currentSession.EnableCardProcessorModule;
}

export function isAttendanceModuleEnabled(): boolean {
	// En el caso del Pos tengo que basarme en la variable attendanceModuleEnabled porque necesito
	// saber si el módulo está activo antes de iniciar sesión para mostrar o no el botón de fichar
	// en la pantalla de login. Esa variable se gestiona desde el GetDeviceConfiguration.
	// En el caso de la administración, no hay un GetDeviceConfiguration, y me tengo que basar en
	// la sesión para saber si está activo o no el informe de fichajes.
	return attendanceModuleEnabled || (!!currentSession && currentSession.EnableAttendanceModule);
}

export function isOnlineReservationModuleEnabled(): boolean {
	return !!currentSession && currentSession.EnableOnlineReservationModule;
}

export function isTablePaymentModuleEnabled(): boolean {
	return !!currentSession && currentSession.EnableTablePaymentModule;
}

export function isSizeAndColorEnabled() {
	return !!currentSession && currentSession.EnableSizeAndColorModule;
}

export function isCashControlModuleEnabled(): boolean {
	return !!currentSession && currentSession.EnableCashControlModule;
}

export function isGStockModuleEnabled(): boolean {
	return !!currentSession && currentSession.EnableGStockModule;
}

export function isPayoutReportEnabled(): boolean {
	return !!currentSession && currentSession.EnablePayoutReport;
}

export function allowPrintTicketGift(): boolean {
	return !!currentSession && currentSession.AllowPrintTicketGift;
}

export function allowFixedTipSettings(): boolean {
	return !!currentSession && currentSession.TipSettingsMode === TipSettingsMode.Fixed;
}

export function deviceAskGratuity(): boolean {
	return !!currentSession && currentSession.DeviceAskGratuity;
}

export function allowSuggestedTipSettings(): boolean {
	return !!currentSession && currentSession.TipSettingsMode === TipSettingsMode.Suggested;
}

export function getSuggestedTipRates(): number[] {
	if (!currentSession)
		return [];

	return currentSession.SuggestedTipRates;
}

export function hasPermission(permission: Permission): boolean {
	return !!currentSession && currentSession.Permissions.includes(permission);
}

export function isLite(): boolean {
	return !!currentSession && currentSession.IsLite;
}

export function isHub(): boolean {
	// No uso la sessión para poder tener esta información incluso antes de logearme
	return INSTALLATION_TYPE === InstallationType.Hub;
}

export function isStandalone(): boolean {
	// No uso la sessión para poder tener esta información incluso antes de logearme
	return INSTALLATION_TYPE === InstallationType.Standalone;
}

export function isHubClient(): boolean {
	// No uso la sessión para poder tener esta información incluso antes de logearme
	return INSTALLATION_TYPE === InstallationType.HubClient;
}

export function isSlave(): boolean {
	return !!currentSession && currentSession.MasterPosId !== 0;
}

export function hasCommonMaster(posId: number): boolean {
	return !!currentSession && [currentSession.PosId, currentSession.MasterPosId].includes(posId);
}

export function setTicketWenPrint(mode: WhenPrint): void {
	if (!currentSession)
		throw new Error('No se puede asignar un modo de impresión si no hay una sesión activa');

	currentSession.TicketWhenPrint = mode;
}

export function getAutoCloseSession(): { enabled: boolean, seconds: number } {
	return {
		enabled: currentSession?.EnableAutoCloseSession ?? false,
		seconds: currentSession?.SecondsToAutoCloseSession ?? 0
	};
}

export function getTicketWhenPrint(): WhenPrint {
	if (!currentSession)
		return WhenPrint.Always;

	return currentSession.TicketWhenPrint;
}

export function getDefaultLocation(): DefaultLocationInSessionDTO {
	if (!currentSession)
		return {
			GoToDefaultLocation: false,
			OnSelectLocationAction: OnSelectLocationAction.LoadOpenedOrCreateNewTicket,
			Location: {
				SaleCenterId: 0,
				SaleCenterName: '',
				SaleLocationName: '',
				AskForFriendlyName: WhenAskForFriendlyName.Never,
				AskForTableCompanions: WhenAskForTableCompanions.Never,
				StartTakeOutOrderOnOpenTicket: false
			}
		};
	return currentSession.DefaultLocation;
}

export function showLastSaleInfoInLoginView(): boolean {
	return cachedShowLastSaleInfoInLoginView;
}

export function showLastSaleInfoInSelectSaleLocationView(): boolean {
	if (!currentSession)
		return false;

	return currentSession.ShowLastSaleInSelectLocationView;
}


